import React, { useCallback, useMemo, useEffect, useRef } from 'react';
import log from 'loglevel';
import { map } from 'ramda';
import { viewPage } from 'fmc-sdk';
import { selectAnalytics, setAnalyticsState } from 'state/analytics';

import { useSelector, useDispatch } from 'react-redux';
import useRouter from 'use-react-router';

import { selectTenant } from 'state/tenant';
import window from 'window';
import useFmcHost from 'hooks/useFmcHost';
import canUseDOM from 'utils/canUseDOM';
import useAnalyticsVariables from './useAnalyticVariables';

export enum Event {
  ApplicationStart,
  ApplicationApproved,
  ApplicationPending,
  ApplicationDuplicate,
  ApplicationFailed,
  AddToWallet,
  ApplicationCompleted,
  TextToRecover,
  ApplyAndBuy,
  PrescreenOfferAccept,
  PrescreenOfferNoThanks,
  PrescreenOfferNotMe,
  PrequalOfferAccept,
  PrequalOfferDecline,
  PrequalOfferText,
  PrequalApplicationSubmit,
  PrequalApplicationReceive,
  EnhancedCheckoutStarted,
  EnhancedCheckoutSuccessful,
  EnhancedCheckoutNotFound,
  EnhancedCheckoutError,
  PageView,
}

const eventMap: {
  [e: number]: {
    name: string;
    linkName?: string;
    customEventDetailType: string;
  };
} = {
  [Event.ApplicationStart]: {
    name: 'event1',
    linkName: 'ApplicationStart',
    customEventDetailType: 'application-start',
  },
  [Event.ApplicationApproved]: {
    name: 'event2',
    linkName: 'ApplicationApproved',
    customEventDetailType: 'application-approved',
  }, // dsp
  [Event.ApplicationPending]: {
    name: 'event3',
    linkName: 'ApplicationPending',
    customEventDetailType: 'application-pending',
  }, // credit-pending
  [Event.ApplicationDuplicate]: {
    name: 'event4',
    linkName: 'ApplicationDuplicate',
    customEventDetailType: 'application-duplicate',
  }, // existing-acconut
  [Event.ApplicationFailed]: {
    name: 'event5',
    linkName: 'ApplicationFailed',
    customEventDetailType: 'application-failed',
  },
  [Event.AddToWallet]: {
    name: 'event6',
    linkName: 'AddToWallet',
    customEventDetailType: 'add-to-wallet',
  },
  [Event.ApplicationCompleted]: {
    name: 'event7',
    linkName: 'ApplicationCompleted',
    customEventDetailType: 'application-completed',
  },
  [Event.TextToRecover]: {
    name: 'event8',
    linkName: 'TextToRecover',
    customEventDetailType: 'text-to-recover',
  },
  [Event.ApplyAndBuy]: {
    name: 'event9',
    linkName: 'ApplyAndBuy',
    customEventDetailType: 'apply-and-buy',
  },
  [Event.PrescreenOfferAccept]: {
    name: 'event10',
    linkName: 'PrescreenOfferAccept',
    customEventDetailType: 'prescreen-offer-acceptance',
  },
  [Event.PrescreenOfferNoThanks]: {
    name: 'event11',
    linkName: 'PrescreenOfferNoThanks',
    customEventDetailType: 'prescreen-offer-no-thanks',
  },
  [Event.PrescreenOfferNotMe]: {
    name: 'event12',
    linkName: 'PrescreenOfferNotMe',
    customEventDetailType: 'prescreen-offer-not-me',
  },
  [Event.PrequalOfferAccept]: {
    name: 'event13',
    linkName: 'PrequalOfferAccept',
    customEventDetailType: 'prequal-offer-acceptance',
  },
  [Event.PrequalOfferDecline]: {
    name: 'event14',
    linkName: 'PrequalOfferDecline',
    customEventDetailType: 'prequal-offer-decline',
  },
  [Event.PrequalOfferText]: {
    name: 'event15',
    linkName: 'PrequalOfferText',
    customEventDetailType: 'prequal-offer-text',
  },
  [Event.EnhancedCheckoutStarted]: {
    name: 'event18',
    linkName: 'EnhancedCheckoutStarted',
    customEventDetailType: 'enhanced-checkout-started',
  },
  [Event.EnhancedCheckoutSuccessful]: {
    name: 'event19',
    linkName: 'EnhancedCheckoutSuccessful',
    customEventDetailType: 'enhanced-checkout-successful',
  },
  [Event.EnhancedCheckoutNotFound]: {
    name: 'event20',
    linkName: 'EnhancedCheckoutNotFound',
    customEventDetailType: 'enhanced-checkout-not-found',
  },
  [Event.EnhancedCheckoutError]: {
    name: 'event21',
    linkName: 'EnhancedCheckoutError',
    customEventDetailType: 'enhanced-checkout-error',
  },
};

export type AnalyticsHook = () => {
  sendEvents: (events: Event[]) => void;
  sendPage: (name?: string) => void;
};

/**
 * Normalize pathname into "pageName"
 */
const usePageName = () => {
  const { location } = useRouter();
  return useMemo(() => location.pathname.replace(/^\//, ''), [location]);
};

/**
 * Wrap any function that requires the s object to be loaded
 * Return a function that will queue all calls until s is loaded. Then it will
 * call the original function with all of the queued arguments.
 *
 * Assume that on window `load` event, the s_code, if not blocked, will have been
 * loaded.
 */
// This function can wrap arbitrary function, so any is necessary.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useDeferredAnalytics<T extends (...args: any[]) => any>(
  callback: T,
): (...args: Parameters<T>) => ReturnType<T> | void {
  const queue = useRef<unknown[][]>([]);
  const listenerRef = useRef<() => void | undefined>();
  const wrapped = useCallback(
    (...args) => {
      if (!canUseDOM) {
        return;
      }
      const hasSCode = () => Boolean(window.s);
      /**
       * If window is already loaded and window.s is still not available,
       * it must have been blocked/missing.
       */
      const isSCodeBlocked = () => {
        return document.readyState === 'complete' && !hasSCode();
      };

      if (hasSCode()) {
        return callback(...args);
      }
      if (isSCodeBlocked()) {
        return log.warn('s_code not loaded.');
      }

      queue.current.push(args);

      const listener = () => {
        if (listenerRef.current) {
          window.removeEventListener('load', listenerRef.current);
        }
        if (isSCodeBlocked()) {
          return log.warn('s_code not loaded.');
        }

        queue.current.forEach(args => {
          callback(...args);
        });
      };
      listenerRef.current = listener;
      window.addEventListener('load', listener);
    },
    [callback],
  );
  return wrapped;
}

const useAnalytics: AnalyticsHook = () => {
  const { analyticsReportSuiteId } = useSelector(selectTenant);
  const { setAnalyticsVariables } = useAnalyticsVariables();
  const fmcDispatch = useFmcHost();

  const defaultPageName = usePageName();

  const sendSiteCatalystAnalyticsEvents = useDeferredAnalytics(
    useCallback(
      (events: Event[]): void => {
        const { s: siteCatalystAnalytics } = window;

        const eventNames = map(e => eventMap[e].name, events).join(',');

        const linkNames = map(e => eventMap[e].linkName, events).join(',');

        if (!siteCatalystAnalytics) {
          return;
        }

        log.debug(
          'Track events: ' + eventNames + ' and link names: ' + linkNames,
        );

        siteCatalystAnalytics.un = analyticsReportSuiteId;
        siteCatalystAnalytics.linkTrackVars = undefined;

        siteCatalystAnalytics.tl(true, 'o', linkNames, {
          events: eventNames,
          linkTrackEvents: eventNames,
        });
      },
      [analyticsReportSuiteId],
    ),
  );

  const sendCustomEvent = useCallback(
    (type: 'event-action-trigger' | 'page-view', detailType?: string) => {
      const customEvent = new CustomEvent(type, {
        detail: detailType ? { type: detailType } : {},
      });
      document.body.dispatchEvent(customEvent);
    },
    [],
  );

  const sendCustomEvents = useCallback(
    (events: Event[]): void => {
      events.forEach((event: Event) => {
        sendCustomEvent(
          'event-action-trigger',
          eventMap[event].customEventDetailType,
        );
      });
    },
    [sendCustomEvent],
  );

  const sendEvents = useCallback(
    (events: Event[]): void => {
      sendCustomEvents(events);
      sendSiteCatalystAnalyticsEvents(events);
    },
    [sendCustomEvents, sendSiteCatalystAnalyticsEvents],
  );

  const sendPageViewCustomEvent = useCallback(
    (): void => sendCustomEvent('page-view'),
    [sendCustomEvent],
  );

  const sendPage = useDeferredAnalytics(
    useCallback(
      (pageName: string = defaultPageName): void => {
        // Analytic events

        fmcDispatch(viewPage(pageName));
        const { s: siteCatalystAnalytics } = window;

        if (!siteCatalystAnalytics) {
          return;
        }

        siteCatalystAnalytics.un = analyticsReportSuiteId;
        siteCatalystAnalytics.pageName = pageName;

        log.debug(`Track pageName: ${siteCatalystAnalytics.pageName}`);

        siteCatalystAnalytics.t();

        // Custom events
        setAnalyticsVariables({ pageName });
        sendPageViewCustomEvent();
      },
      [
        analyticsReportSuiteId,
        defaultPageName,
        fmcDispatch,
        setAnalyticsVariables,
        sendPageViewCustomEvent,
      ],
    ),
  );

  return { sendEvents, sendPage };
};

const useTrackApplicationStart = (pageName?: string, event?: Event) => {
  const { applicationStarted } = useSelector(selectAnalytics);
  const { sendEvents, sendPage } = useAnalytics();
  const defaultPageName = usePageName();
  const dispatch = useDispatch();
  const fmcDispatch = useFmcHost();

  useEffect(() => {
    const finalPageName = pageName || defaultPageName;
    if (applicationStarted) {
      sendPage(finalPageName);
    } else {
      sendEvents([event || Event.ApplicationStart]);
      sendPage(finalPageName);
      fmcDispatch(viewPage(finalPageName));
      dispatch(setAnalyticsState({ applicationStarted: true }));
    }
    // This should only run once when the page load. We don't need to
    // run it again when applicationStarted changes (because it is changed
    // inside this block)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

/**
 * Route wrapper that track page view on every navigation event.
 *
 * It uses the pathname as the page name for analytics.
 *
 * It will also sends event names if configured. See `pageEvents` above
 */
const TrackedRoute: React.FC = ({ children }) => {
  const { sendPage } = useAnalytics();
  const { location } = useRouter();
  useEffect(() => {
    if (location) {
      sendPage();
    }
  }, [sendPage, location]);
  return <React.Fragment>{children}</React.Fragment>;
};

export default useAnalytics;
export { TrackedRoute, usePageName, useTrackApplicationStart };
